import React, { useContext, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import AddNewIcon from 'assets/icons/add-new'
import SpeciesParameter from './speciesParameter'
import { requestDeleteSiteSpecieAssesmentParameter } from 'api/siteSpecieAssesmentParameter'
import { requestUpdateSiteSpecie } from 'api/siteSpecie'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import Textarea from 'components/Textarea'
import MultiSelectionDropdown from 'components/multiSelectDropdown'
import { SiteContext } from 'providers/site'
import { ProfileContext } from 'providers/profile'
import { delay } from 'utils/delay'
import Checkbox from 'components/Checkbox'
import { showErrorToast } from 'utils/toast'
import { canBirdLifeSecretariatDelistOrSupersede } from 'utils/siteIbaAccess'
import { useOverallKbaCriteriaRefresh } from 'flow/siteProposal/pages/assessment/context/OverallKbaCriteriaRefreshContext'
import {
  AssessmentPageSubTittle,
  AssessmentPageSubTittleIconWrapper,
  AssessmentPageSubTittleWrapper,
  AssessmentPageTittle,
  AssessmentPageTittleWrapper
} from '../../../../style'
import {
  SpeciesDetailsAddParameter,
  SpeciesDetailsContainer
} from '../style'
import { congregatoryGroupOptions } from 'constants/parametersOptions'

const IrreversibleIndex = 7
const ReversibleIndex = 8
const qualifyIndex = 0
const reasonDefaultOptions = [
  { label: 'Qualifies', value: 1, disabled: false },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Taxonomic revision', value: 4, disabled: false },
  { label: 'Revision to Red List status', value: 5, disabled: false },
  { label: 'Revision of global threshold', value: 6, disabled: false },
  { label: 'No longer geographically restricted', value: 7, disabled: false },
  { label: 'Genuine decline, irreversible', value: 8, disabled: false },
  { label: 'Genuine decline, reversible', value: 9, disabled: false },
  { label: 'Genuine decline, other factors', value: 10, disabled: false }
]

const reasonQualifyOptions = [
  { label: 'Qualifies', value: 1, disabled: false },
  { label: 'Improved site information', value: 2, disabled: true },
  { label: 'KBA criteria revision', value: 3, disabled: true },
  { label: 'Taxonomic revision', value: 4, disabled: true },
  { label: 'Revision to Red List status', value: 5, disabled: true },
  { label: 'Revision of global threshold', value: 6, disabled: true },
  { label: 'No longer geographically restricted', value: 7, disabled: true },
  { label: 'Genuine decline, irreversible', value: 8, disabled: true },
  { label: 'Genuine decline, reversible', value: 9, disabled: true },
  { label: 'Genuine decline, other factors', value: 10, disabled: true }
]

const reasonNonQualifyOptions = [
  { label: 'Qualifies', value: 1, disabled: true },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Taxonomic revision', value: 4, disabled: false },
  { label: 'Revision to Red List status', value: 5, disabled: false },
  { label: 'Revision of global threshold', value: 6, disabled: false },
  { label: 'No longer geographically restricted', value: 7, disabled: false },
  { label: 'Genuine decline, irreversible', value: 8, disabled: false },
  { label: 'Genuine decline, reversible', value: 9, disabled: false },
  { label: 'Genuine decline, other factors', value: 10, disabled: false }
]

const reasonOptionsIrreversibleSelected = [
  { label: 'Qualifies', value: 1, disabled: true },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Taxonomic revision', value: 4, disabled: false },
  { label: 'Revision to Red List status', value: 5, disabled: false },
  { label: 'Revision of global threshold', value: 6, disabled: false },
  { label: 'No longer geographically restricted', value: 7, disabled: false },
  { label: 'Genuine decline, irreversible', value: 8, disabled: false },
  { label: 'Genuine decline, reversible', value: 9, disabled: true },
  { label: 'Genuine decline, other factors', value: 10, disabled: false }
]

const reasonOptionsReversibleSelected = [
  { label: 'Qualifies', value: 1, disabled: true },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Taxonomic revision', value: 4, disabled: false },
  { label: 'Revision to Red List status', value: 5, disabled: false },
  { label: 'Revision of global threshold', value: 6, disabled: false },
  { label: 'No longer geographically restricted', value: 7, disabled: false },
  { label: 'Genuine decline, irreversible', value: 8, disabled: true },
  { label: 'Genuine decline, reversible', value: 9, disabled: false },
  { label: 'Genuine decline, other factors', value: 10, disabled: false }
]

const tooltipXOffset = 0
const tooltipYOffset = 16
const maximumParameterListLength = 24
const inputSaveDelayTime = 300
const updateSensitiveSourceTimeOut = delay(inputSaveDelayTime)

function getSensitiveAtSiteFromItem (item) {
  return Object.hasOwn(item, 'sensitive_at_site')
    ? item.sensitive_at_site
    : item.sensitiveAtSite
}

function getSensitiveAtSiteSourceFromItem (item) {
  return Object.hasOwn(item, 'sensitive_at_site_source')
    ? item.sensitive_at_site_source
    : item.sensitiveAtSiteSource
}

function getDisqualifiedIbaTriggerFromItem (item) {
  if (Object.hasOwn(item, 'disqualified_iba_trigger')) {
    return item.disqualified_iba_trigger
  }

  if (Object.hasOwn(item, 'ibaDisqualified')) {
    return item.ibaDisqualified
  }

  return item.disqualifiedIbaTrigger
}

function buildSpeciesValidationUpdate (prev, specieIdForValidation, isValid) {
  if (!prev?.species || typeof prev.species !== 'object') {
    return prev
  }

  const isFlatSpeciesValidation =
    Object.hasOwn(prev.species, 'scientificName') ||
    Object.hasOwn(prev.species, 'specieName') ||
    Object.hasOwn(prev.species, 'triggerReasonId')

  if (isFlatSpeciesValidation) {
    return {
      ...prev,
      species: {
        ...prev.species,
        rationalForReassessmentSpecies: isValid
      }
    }
  }

  return {
    ...prev,
    species: {
      ...prev.species,
      [specieIdForValidation]: {
        ...(prev.species?.[specieIdForValidation]),
        rationalForReassessmentSpecies: isValid
      }
    }
  }
}

function hasSensitiveAtSiteSourceFieldError (error) {
  const failedFields = error?.failedFields
  return Array.isArray(failedFields) && failedFields.includes('sensitive_at_site_source')
}

function getInitialParameterList (item) {
  return item.SpeciesAssesmentParameters.length ? item.SpeciesAssesmentParameters : [{ id: 'default' }]
}

function getInitialReassessmentRational (item) {
  return item.triggerReasonText || ''
}

function getInitialCongregatoryGroup (item) {
  return congregatoryGroupOptions.find((element) => {
    return element.value === item.CongregationGroupId
  }) || null
}

function getInitialSensitiveAtSiteSource (item) {
  const isSensitive = getSensitiveAtSiteFromItem(item)
  if (!isSensitive) {
    return ''
  }

  const raw = getSensitiveAtSiteSourceFromItem(item)
  return raw || ''
}

function sortParametersByCreatedAt (parameterList) {
  return [...parameterList].sort((a, b) => {
    if (a.createdAt > b.createdAt) {
      return 1
    }

    if (a.createdAt < b.createdAt) {
      return -1
    }

    return 0
  })
}

function deriveFormState ({
  item,
  site,
  profile,
  isEditable,
  qualificationReason,
  siteProposalFieldValidation,
  disqualifiedIbaTrigger,
  reassessmentRational,
  sensitiveAtSite,
  sensitiveAtSiteSource,
  sensitiveAtSiteSourceError
}) {
  const qualified = item.lastReassessmentDate
    ? Boolean(item.triggerReasonId?.includes(1))
    : true
  const showReassessmentSection = item.lastReassessmentDate !== null && site.reassessmentFlag
  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)
  const canDelistIbaForCurrentSite = canBirdLifeSecretariatDelistOrSupersede(site, profile)
  const shouldShowIbaDisqualificationSection = (item.lastReassessmentDate !== null && site.reassessmentFlag) || canDelistIbaForCurrentSite
  const hasIBAAccessForIbaDelist = Boolean(site?.ibaAccess?.hasAccess || item?.ibaAccess?.hasAccess)
  const shouldRenderIbaDisqualifiedCheckbox = shouldShowIbaDisqualificationSection && hasIBAAccessForIbaDelist
  const hasDisqualifyingReasonSelected = Array.isArray(qualificationReason) &&
    qualificationReason.some((reasonId) => Number(reasonId) > 1)
  const hasValidationRun = Boolean(siteProposalFieldValidation?.updated)
  const shouldRequireDisqualifiedIbaDetails =
    hasValidationRun && disqualifiedIbaTrigger && !reassessmentRational?.trim()
  const trimmedSensitiveSource = sensitiveAtSiteSource?.trim()
  const isSensitiveSourceMissing = sensitiveAtSite && (trimmedSensitiveSource === undefined || trimmedSensitiveSource === '')
  const derivedSensitiveSourceError = sensitiveAtSiteSourceError || (
    hasValidationRun && isSensitiveSourceMissing
      ? 'sensitiveAtSiteSourceRequired'
      : ''
  )

  return {
    qualified,
    showReassessmentSection,
    disabled,
    shouldShowIbaDisqualificationSection,
    shouldRenderIbaDisqualifiedCheckbox,
    hasDisqualifyingReasonSelected,
    hasValidationRun,
    shouldRequireDisqualifiedIbaDetails,
    derivedSensitiveSourceError
  }
}

const AssessmentHeaderSection = ({ showReassessmentSection }) => (
  <Container>
    <Row>
      <Col md={8} mdOffset={2}>
        <AssessmentPageTittleWrapper marginBottom={showReassessmentSection ? '64px' : '0px'}>
          <AssessmentPageTittle data-test='legend'>
            <FormattedMessage id='assessment' />
          </AssessmentPageTittle>
          <AssessmentPageSubTittleWrapper>
            <AssessmentPageSubTittle>
              <FormattedMessage id='speciesAssessmentSubTittle' />
            </AssessmentPageSubTittle>
            <Tooltip
              style={{ zIndex: 1000 }}
              zIndex='1070'
              placement='bottom'
              trigger={['hover']}
              align={{ offset: [tooltipXOffset, tooltipYOffset] }}
              overlay={{ label: 'assessment', infoTooltip: 'speciesAssessmentSubTittleHint' }}
            >
              <AssessmentPageSubTittleIconWrapper>
                <InfoIcon />
              </AssessmentPageSubTittleIconWrapper>
            </Tooltip>
          </AssessmentPageSubTittleWrapper>
        </AssessmentPageTittleWrapper>
      </Col>
    </Row>
  </Container>
)

const AddParameterSection = ({ parameterListLength, addParameter, disabled }) => {
  if (parameterListLength >= maximumParameterListLength) {
    return null
  }

  return (
    <SpeciesDetailsContainer>
      <SpeciesDetailsAddParameter onClick={addParameter} disabled={disabled}>
        <AddNewIcon />
        <FormattedMessage id='newParameter' />
      </SpeciesDetailsAddParameter>
    </SpeciesDetailsContainer>
  )
}

const SensitiveAtSiteSection = ({
  sensitiveAtSite,
  handleToggleSensitiveAtSite,
  disabled,
  sensitiveAtSiteSource,
  handleChangeSensitiveAtSiteSource,
  derivedSensitiveSourceError
}) => (
  <SpeciesDetailsContainer>
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '16px' }}>
        <Checkbox
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FormattedMessage id='sensitiveAtSiteLabel' />
              <Tooltip
                style={{ zIndex: 1000 }}
                zIndex='1070'
                placement='bottom'
                trigger={['hover']}
                align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                overlay={{ label: 'sensitiveAtSiteLabel', infoTooltip: 'sensitiveAtSiteHint' }}
              >
                <AssessmentPageSubTittleIconWrapper>
                  <InfoIcon />
                </AssessmentPageSubTittleIconWrapper>
              </Tooltip>
            </span>
          }
          checked={Boolean(sensitiveAtSite)}
          onChange={handleToggleSensitiveAtSite}
          field='sensitiveAtSite'
          disabled={disabled}
        />
      </div>
      <Textarea
        value={sensitiveAtSiteSource}
        onChange={handleChangeSensitiveAtSiteSource}
        placeholder='sensitiveAtSiteSourcePlaceholder'
        label={
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FormattedMessage id='sensitiveAtSiteSource' />
            {sensitiveAtSite && <span style={{ color: '#d32f2f' }}>*</span>}
          </span>
        }
        infoTooltip='sensitiveAtSiteSourceHint'
        field='sensitiveAtSiteSource'
        height='128px'
        disabled={disabled || sensitiveAtSite !== true}
        zIndex='1070'
        error={
          sensitiveAtSite && derivedSensitiveSourceError
            ? { field: 'sensitiveAtSiteSource', error: derivedSensitiveSourceError }
            : { field: '', error: '' }
        }
      />
    </div>
  </SpeciesDetailsContainer>
)

const IbaDisqualificationSection = ({
  shouldShowIbaDisqualificationSection,
  showTriggerDropdown,
  qualificationReason,
  handleChangeQualificationReason,
  handleChangeValueChecked,
  checkedOptions,
  checkFields,
  shouldRenderIbaDisqualifiedCheckbox,
  disqualifiedIbaTrigger,
  handleToggleDisqualifiedIbaTrigger,
  disabled,
  hasDisqualifyingReasonSelected
}) => {
  if (!shouldShowIbaDisqualificationSection) {
    return null
  }

  return (
    <SpeciesDetailsContainer>
      {showTriggerDropdown && (
        <MultiSelectionDropdown
          value={qualificationReason}
          saveChanges={handleChangeQualificationReason}
          valueChecked={handleChangeValueChecked}
          label='triggerLabel'
          infoTooltip='triggerLabelHint'
          field='qualificationReasonSpecies'
          marginBottom='64px'
          menuWidth='416px'
          secondStepOptions={checkedOptions}
          placeholder='specieTriggerPlaceholder'
          fullWidthSelect={true}
          zIndex='1070'
          error={
            shouldValidateSpeciesField(qualificationReason, checkFields, 'qualificationReasonSpecies')
              ? {
                  field: '',
                  error: ''
                }
              : { field: 'qualificationReasonSpecies', error: 'pleaseCompleteThisField' }
          }
        />
      )}
      {shouldRenderIbaDisqualifiedCheckbox && (
        <div style={{ width: '100%', marginBottom: '16px', position: 'relative' }}>
          <Checkbox
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FormattedMessage id='disqualifiedIbaTriggerLabel' />
                <Tooltip
                  style={{ zIndex: 1000 }}
                  zIndex='1070'
                  placement='bottom'
                  trigger={['hover']}
                  align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                  overlay={{ label: 'disqualifiedIbaTriggerLabel', infoTooltip: 'disqualifiedIbaTriggerHint' }}
                >
                  <AssessmentPageSubTittleIconWrapper>
                    <InfoIcon />
                  </AssessmentPageSubTittleIconWrapper>
                </Tooltip>
              </span>
            }
            checked={Boolean(disqualifiedIbaTrigger)}
            onChange={handleToggleDisqualifiedIbaTrigger}
            field='disqualifiedIbaTrigger'
            disabled={disabled || !hasDisqualifyingReasonSelected}
            isOptional={true}
          />
        </div>
      )}
    </SpeciesDetailsContainer>
  )
}

const ReassessmentRationalSection = ({
  shouldShowIbaDisqualificationSection,
  reassessmentRational,
  handleChangeReassessmentRational,
  disabled,
  shouldRequireDisqualifiedIbaDetails
}) => (
  <SpeciesDetailsContainer>
    <div style={{ width: '100%' }}>
      {shouldShowIbaDisqualificationSection && (
        <Textarea
          value={reassessmentRational}
          onChange={handleChangeReassessmentRational}
          placeholder='justificationNoChangeSpeciePlaceholder'
          label='justificationNoChange'
          infoTooltip='qualificationReasonHint'
          field='rationalForReassessmentSpecies'
          height='128px'
          disabled={disabled}
          zIndex='1070'
          error={
            shouldRequireDisqualifiedIbaDetails
              ? { field: 'rationalForReassessmentSpecies', error: 'pleaseCompleteThisField' }
              : { field: '', error: '' }
          }
        />
      )}
    </div>
    <div style={{ height: '150px', width: '100%' }} />
  </SpeciesDetailsContainer>
)

function resolveCheckedOptions (Ids) {
  const isAnyNonQualifyOptionsSelected = Ids.some((item) => {
    return item.checked && item.value !== 1
  })

  if (Ids[IrreversibleIndex].checked) {
    return reasonOptionsIrreversibleSelected
  }

  if (Ids[ReversibleIndex].checked) {
    return reasonOptionsReversibleSelected
  }

  if (Ids[qualifyIndex].checked) {
    return reasonQualifyOptions
  }

  if (isAnyNonQualifyOptionsSelected) {
    return reasonNonQualifyOptions
  }

  return reasonDefaultOptions
}

function shouldValidateSpeciesField (qualificationReason, checkFields, fieldName) {
  if (qualificationReason?.length === 0) {
    return checkFields[fieldName]
  }

  return true
}

function getIbaTogglePayload (newValue, hasDisqualifyingReasonSelected, ibaCriterias, reassessmentRational) {
  return {
    disqualified_iba_trigger: newValue,
    ibaCriterias: (newValue && hasDisqualifyingReasonSelected) ? [] : ibaCriterias,
    triggerReasonText: newValue ? (reassessmentRational?.trim() || null) : reassessmentRational,
    reassessDateStatus: true
  }
}

function syncSensitiveAtSiteToggle ({
  newValue,
  sensitiveAtSiteSource,
  item,
  params,
  getSiteSpecie,
  setSensitiveAtSite,
  setSensitiveAtSiteSource,
  setSensitiveAtSiteSourceError
}) {
  if (newValue) {
    const trimmedSource = sensitiveAtSiteSource ? sensitiveAtSiteSource.trim() : null
    requestUpdateSiteSpecie(
      item.id,
      params.siteHistoryId,
      item.historyId,
      {
        sensitiveAtSite: true,
        sensitiveAtSiteSource: trimmedSource,
        reassessDateStatus: true
      }
    ).then(() => {
      getSiteSpecie()
    }).catch((error) => {
      if (hasSensitiveAtSiteSourceFieldError(error)) {
        setSensitiveAtSiteSourceError(error.message || 'sensitiveAtSiteSourceRequired')
        return
      }
      setSensitiveAtSite(false)
      getSiteSpecie()
    })

    return
  }

  setSensitiveAtSiteSource('')
  requestUpdateSiteSpecie(
    item.id,
    params.siteHistoryId,
    item.historyId,
    {
      sensitiveAtSite: false,
      sensitiveAtSiteSource: null,
      reassessDateStatus: true
    }
  ).then(() => getSiteSpecie()).catch(() => {
    getSiteSpecie()
  })
}

async function updateQualificationReason ({
  triggerReasonId,
  itemId,
  siteHistoryId,
  historyId,
  criteria
}) {
  const payload = {
    triggerReasonId,
    reassessDateStatus: true
  }

  if (triggerReasonId <= 1) {
    payload.criteria = criteria
  }

  await requestUpdateSiteSpecie(itemId, siteHistoryId, historyId, payload)
}

function useTriggerReasonEffects ({
  item,
  setParameterList,
  qualificationReason,
  updateTriggerReasonId,
  setReassessmentRational,
  setQualificationReason,
  setCongregatoryGroup
}) {
  useEffect(() => {
    if (item.SpeciesAssesmentParameters.length) {
      setParameterList(item.SpeciesAssesmentParameters)
      return
    }
    setParameterList([{ id: 'default' }])
  }, [item, setParameterList])

  useEffect(() => {
    updateTriggerReasonId(qualificationReason)
  }, [qualificationReason, updateTriggerReasonId])

  useEffect(() => {
    setReassessmentRational(item.triggerReasonText ? item.triggerReasonText : '')
  }, [item.triggerReasonText, setReassessmentRational])

  useEffect(() => {
    setQualificationReason(item.triggerReasonId || [])
  }, [item.triggerReasonId, setQualificationReason])

  useEffect(() => {
    const newCongregatoryGroup = congregatoryGroupOptions.find((element) => {
      return element.value === item.CongregationGroupId
    }) || null
    setCongregatoryGroup(newCongregatoryGroup)
  }, [item.CongregationGroupId, setCongregatoryGroup])
}

function useSensitiveEffects ({
  item,
  setSensitiveAtSite,
  setSensitiveAtSiteSourceError,
  setSensitiveAtSiteSource,
  setDisqualifiedIbaTrigger
}) {
  useEffect(() => {
    const dbValue = getSensitiveAtSiteFromItem(item)
    setSensitiveAtSite(Boolean(dbValue))
  }, [item.sensitive_at_site, item.sensitiveAtSite, setSensitiveAtSite])

  useEffect(() => {
    setSensitiveAtSiteSourceError('')
    const isSensitive = getSensitiveAtSiteFromItem(item)
    if (isSensitive) {
      const sourceValue = getSensitiveAtSiteSourceFromItem(item)
      setSensitiveAtSiteSource(sourceValue || '')
      return
    }
    setSensitiveAtSiteSource('')
  }, [item.sensitive_at_site_source, item.sensitiveAtSiteSource, item.sensitive_at_site, item.sensitiveAtSite, setSensitiveAtSiteSourceError, setSensitiveAtSiteSource])

  useEffect(() => {
    const dbValue = getDisqualifiedIbaTriggerFromItem(item)
    setDisqualifiedIbaTrigger(Boolean(dbValue))
  }, [item.disqualified_iba_trigger, item.ibaDisqualified, item.disqualifiedIbaTrigger, setDisqualifiedIbaTrigger])
}

function useValidationEffects ({
  hasValidationRun,
  disqualifiedIbaTrigger,
  reassessmentRational,
  specieIdForValidation,
  setSiteProposalFieldValidation,
  hasDisqualifyingReasonSelected,
  item,
  params,
  setDisqualifiedIbaTrigger
}) {
  useEffect(() => {
    if (hasValidationRun !== true) return

    const isValid = !disqualifiedIbaTrigger || Boolean(reassessmentRational?.trim())
    setSiteProposalFieldValidation((prev) => buildSpeciesValidationUpdate(prev, specieIdForValidation, isValid))
  }, [hasValidationRun, disqualifiedIbaTrigger, reassessmentRational, specieIdForValidation, setSiteProposalFieldValidation])

  useEffect(() => {
    if (!disqualifiedIbaTrigger || hasDisqualifyingReasonSelected) {
      return
    }

    setDisqualifiedIbaTrigger(false)
    requestUpdateSiteSpecie(
      item.id,
      params.siteHistoryId,
      item.historyId,
      {
        disqualified_iba_trigger: false,
        reassessDateStatus: true
      }
    ).catch(() => {})
  }, [disqualifiedIbaTrigger, hasDisqualifyingReasonSelected, item.id, item.historyId, params.siteHistoryId, setDisqualifiedIbaTrigger])
}

function useSpeciesAssessmentHandlers ({
  disabled,
  setQualificationReason,
  item,
  params,
  setCheckedOptions,
  checkedValue,
  setReassessmentRational,
  parameterList,
  setParameterList,
  getSiteSpecie,
  refetchCriteria,
  setCongregatoryGroup,
  setSensitiveAtSite,
  setSensitiveAtSiteSourceError,
  sensitiveAtSite,
  sensitiveAtSiteSource,
  setSensitiveAtSiteSource,
  setDisqualifiedIbaTrigger,
  disqualifiedIbaTrigger,
  hasDisqualifyingReasonSelected,
  reassessmentRational,
  intl
}) {
  const isForbiddenError = (error) => {
    const status = error?.status || error?.statusCode || error?.response?.status
    return status === 403
  }

  const handleChangeQualificationReason = async (e) => {
    try {
      setQualificationReason(e)
      await updateQualificationReason({
        triggerReasonId: e,
        itemId: item.id,
        siteHistoryId: params.siteHistoryId,
        historyId: item.historyId,
        criteria: item.criteria
      })
    } catch {
      // Trigger reason update failed
    }
  }

  const handleChangeValueChecked = (Ids) => {
    setCheckedOptions(resolveCheckedOptions(Ids))
    checkedValue.current = Ids
  }

  const handleChangeReassessmentRational = (e) => {
    setReassessmentRational(e.target.value)

    requestUpdateSiteSpecie(item.id, params.siteHistoryId, item.historyId, { triggerReasonText: e.target.value, reassessDateStatus: true })
  }

  const addParameter = () => {
    setParameterList([...parameterList, { id: `parameter${Math.random()}`, isMaturePresent: false }])
  }

  const removeParameter = (id) => {
    setParameterList(
      parameterList.filter((element) => {
        return element.id !== id
      })
    )

    requestDeleteSiteSpecieAssesmentParameter(id, params.siteHistoryId, item.id, item.historyId).then(() => {
      getSiteSpecie()
      refetchCriteria()
    })
  }

  const updateParameterList = (updtItem) => {
    setParameterList((s) => {
      const tmpCopy = [...s]
      const found = s.findIndex((element) => {
        return element.id === updtItem.id
      })
      tmpCopy[found] = { ...updtItem }
      return tmpCopy
    })
    refetchCriteria()
  }

  const handleChangeCongregatoryGroup = (e) => {
    setCongregatoryGroup(e)
    requestUpdateSiteSpecie(item.id, params.siteHistoryId, item.historyId, { CongregationGroupId: e.value, reassessDateStatus: true })
  }

  const handleToggleSensitiveAtSite = () => {
    if (disabled) {
      return
    }

    const newValue = !sensitiveAtSite
    setSensitiveAtSite(newValue)
    setSensitiveAtSiteSourceError('')
    syncSensitiveAtSiteToggle({
      newValue,
      sensitiveAtSiteSource,
      item,
      params,
      getSiteSpecie,
      setSensitiveAtSite,
      setSensitiveAtSiteSource,
      setSensitiveAtSiteSourceError
    })
  }

  const handleChangeSensitiveAtSiteSource = (e) => {
    const value = e.target.value
    setSensitiveAtSiteSource(value)
    setSensitiveAtSiteSourceError('')

    const trimmedValue = value ? value.trim() : null

    updateSensitiveSourceTimeOut(() => {
      requestUpdateSiteSpecie(
        item.id,
        params.siteHistoryId,
        item.historyId,
        {
          sensitiveAtSite,
          sensitiveAtSiteSource: trimmedValue,
          reassessDateStatus: true
        }
      ).catch((error) => {
        if (hasSensitiveAtSiteSourceFieldError(error)) {
          setSensitiveAtSiteSourceError(error.message || 'sensitiveAtSiteSourceRequired')
        }
      })
    })
  }

  const handleToggleDisqualifiedIbaTrigger = () => {
    if (disabled) {
      return
    }

    const newValue = !disqualifiedIbaTrigger
    setDisqualifiedIbaTrigger(newValue)

    requestUpdateSiteSpecie(
      item.id,
      params.siteHistoryId,
      item.historyId,
      getIbaTogglePayload(newValue, hasDisqualifyingReasonSelected, item.ibaCriterias, reassessmentRational)
    ).catch((error) => {
      if (isForbiddenError(error)) {
        showErrorToast(intl.formatMessage({ id: 'ibaDisqualificationForbidden' }), 'showBorder')
      }
      setDisqualifiedIbaTrigger(!newValue)
    })
  }

  return {
    handleChangeQualificationReason,
    handleChangeValueChecked,
    handleChangeReassessmentRational,
    addParameter,
    removeParameter,
    updateParameterList,
    handleChangeCongregatoryGroup,
    handleToggleSensitiveAtSite,
    handleChangeSensitiveAtSiteSource,
    handleToggleDisqualifiedIbaTrigger
  }
}

const SpeciesDetailsAssessmentForm = ({
  item,
  getSiteSpecie,
  setItem,
  checkFields,
  site,
  setSiteProposalFieldValidation,
  isEditable
}) => {
  const intl = useIntl()
  const params = useParams()
  const { profile } = useContext(ProfileContext)
  const checkedValue = useRef([])
  const [parameterList, setParameterList] = useState(getInitialParameterList(item))
  const [qualificationReason, setQualificationReason] = useState(item.triggerReasonId || [])
  const [reassessmentRational, setReassessmentRational] = useState(getInitialReassessmentRational(item))
  const [checkedOptions, setCheckedOptions] = useState(reasonDefaultOptions)
  const [congregatoryGroup, setCongregatoryGroup] = useState(getInitialCongregatoryGroup(item))
  const [sensitiveAtSite, setSensitiveAtSite] = useState(() => {
    const dbValue = getSensitiveAtSiteFromItem(item)
    return Boolean(dbValue)
  })
  const [sensitiveAtSiteSource, setSensitiveAtSiteSource] = useState(getInitialSensitiveAtSiteSource(item))
  const [sensitiveAtSiteSourceError, setSensitiveAtSiteSourceError] = useState('')
  const [disqualifiedIbaTrigger, setDisqualifiedIbaTrigger] = useState(() => {
    const dbValue = getDisqualifiedIbaTriggerFromItem(item)
    return Boolean(dbValue)
  })
  const { updateTriggerReasonId, siteProposalFieldValidation } = useContext(SiteContext)
  const { refetchCriteria } = useOverallKbaCriteriaRefresh()

  const {
    qualified,
    showReassessmentSection,
    disabled,
    shouldShowIbaDisqualificationSection,
    shouldRenderIbaDisqualifiedCheckbox,
    hasDisqualifyingReasonSelected,
    hasValidationRun,
    shouldRequireDisqualifiedIbaDetails,
    derivedSensitiveSourceError
  } = deriveFormState({
    item,
    site,
    profile,
    isEditable,
    qualificationReason,
    siteProposalFieldValidation,
    disqualifiedIbaTrigger,
    reassessmentRational,
    sensitiveAtSite,
    sensitiveAtSiteSource,
    sensitiveAtSiteSourceError
  })

  const {
    handleChangeQualificationReason,
    handleChangeValueChecked,
    handleChangeReassessmentRational,
    addParameter,
    removeParameter,
    updateParameterList,
    handleChangeCongregatoryGroup,
    handleToggleSensitiveAtSite,
    handleChangeSensitiveAtSiteSource,
    handleToggleDisqualifiedIbaTrigger
  } = useSpeciesAssessmentHandlers({
    disabled,
    setQualificationReason,
    item,
    params,
    setCheckedOptions,
    checkedValue,
    setReassessmentRational,
    parameterList,
    setParameterList,
    getSiteSpecie,
    refetchCriteria,
    setCongregatoryGroup,
    setSensitiveAtSite,
    setSensitiveAtSiteSourceError,
    sensitiveAtSite,
    sensitiveAtSiteSource,
    setSensitiveAtSiteSource,
    setDisqualifiedIbaTrigger,
    disqualifiedIbaTrigger,
    hasDisqualifyingReasonSelected,
    reassessmentRational,
    intl
  })

  const renderParametersList = sortParametersByCreatedAt(parameterList).map((parameter) => {
    return (
      <SpeciesParameter
        key={parameter.id}
        getSiteSpecie={getSiteSpecie}
        speciesId={item.id}
        removeOnCLik={removeParameter}
        parameter={parameter}
        updateParameterList={updateParameterList}
        setItem={setItem}
        checkParameterField={checkFields.numberOfAssessmentParameters}
        species={item}
        site={site}
        qualified={qualified}
        setSiteProposalFieldValidation={setSiteProposalFieldValidation}
        congregatoryGroup={congregatoryGroup}
        handleChangeCongregatoryGroup={handleChangeCongregatoryGroup}
      />
    )
  })

  const specieIdForValidation = item.reassessmentFlag ? item.historyId : item.id

  useTriggerReasonEffects({
    item,
    setParameterList,
    qualificationReason,
    updateTriggerReasonId,
    setReassessmentRational,
    setQualificationReason,
    setCongregatoryGroup
  })

  useSensitiveEffects({
    item,
    setSensitiveAtSite,
    setSensitiveAtSiteSourceError,
    setSensitiveAtSiteSource,
    setDisqualifiedIbaTrigger
  })

  useValidationEffects({
    hasValidationRun,
    disqualifiedIbaTrigger,
    reassessmentRational,
    specieIdForValidation,
    setSiteProposalFieldValidation,
    hasDisqualifyingReasonSelected,
    item,
    params,
    setDisqualifiedIbaTrigger
  })

  return (
    <>
      <AssessmentHeaderSection showReassessmentSection={showReassessmentSection} />
      {renderParametersList}
      <AddParameterSection
        parameterListLength={parameterList.length}
        addParameter={addParameter}
        disabled={disabled}
      />
      <SensitiveAtSiteSection
        sensitiveAtSite={sensitiveAtSite}
        handleToggleSensitiveAtSite={handleToggleSensitiveAtSite}
        disabled={disabled}
        sensitiveAtSiteSource={sensitiveAtSiteSource}
        handleChangeSensitiveAtSiteSource={handleChangeSensitiveAtSiteSource}
        derivedSensitiveSourceError={derivedSensitiveSourceError}
      />
      <IbaDisqualificationSection
        shouldShowIbaDisqualificationSection={shouldShowIbaDisqualificationSection}
        showTriggerDropdown={item.lastReassessmentDate !== null && site.reassessmentFlag}
        qualificationReason={qualificationReason}
        handleChangeQualificationReason={handleChangeQualificationReason}
        handleChangeValueChecked={handleChangeValueChecked}
        checkedOptions={checkedOptions}
        checkFields={checkFields}
        shouldRenderIbaDisqualifiedCheckbox={shouldRenderIbaDisqualifiedCheckbox}
        disqualifiedIbaTrigger={disqualifiedIbaTrigger}
        handleToggleDisqualifiedIbaTrigger={handleToggleDisqualifiedIbaTrigger}
        disabled={disabled}
        hasDisqualifyingReasonSelected={hasDisqualifyingReasonSelected}
      />
      <ReassessmentRationalSection
        shouldShowIbaDisqualificationSection={shouldShowIbaDisqualificationSection}
        reassessmentRational={reassessmentRational}
        handleChangeReassessmentRational={handleChangeReassessmentRational}
        disabled={disabled}
        shouldRequireDisqualifiedIbaDetails={shouldRequireDisqualifiedIbaDetails}
      />
    </>
  )
}

const speciesItemPropTypes = {
  SpeciesAssesmentParameters: PropTypes.array,
  triggerReasonId: PropTypes.array,
  triggerReasonText: PropTypes.string,
  CongregationGroupId: PropTypes.number,
  reassessmentFlag: PropTypes.bool,
  sensitive_at_site: PropTypes.bool,
  sensitiveAtSite: PropTypes.bool,
  sensitive_at_site_source: PropTypes.string,
  sensitiveAtSiteSource: PropTypes.string,
  disqualified_iba_trigger: PropTypes.bool,
  ibaDisqualified: PropTypes.bool,
  disqualifiedIbaTrigger: PropTypes.bool,
  ibaCriterias: PropTypes.array,
  ibaAccess: PropTypes.shape({
    hasAccess: PropTypes.bool
  }),
  lastReassessmentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.instanceOf(Date)]),
  id: PropTypes.number.isRequired,
  historyId: PropTypes.number.isRequired,
  criteria: PropTypes.array
}

AssessmentHeaderSection.propTypes = {
  showReassessmentSection: PropTypes.bool.isRequired
}

AddParameterSection.propTypes = {
  parameterListLength: PropTypes.number.isRequired,
  addParameter: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired
}

SensitiveAtSiteSection.propTypes = {
  sensitiveAtSite: PropTypes.bool.isRequired,
  handleToggleSensitiveAtSite: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  sensitiveAtSiteSource: PropTypes.string.isRequired,
  handleChangeSensitiveAtSiteSource: PropTypes.func.isRequired,
  derivedSensitiveSourceError: PropTypes.string
}

IbaDisqualificationSection.propTypes = {
  shouldShowIbaDisqualificationSection: PropTypes.bool.isRequired,
  showTriggerDropdown: PropTypes.bool.isRequired,
  qualificationReason: PropTypes.array.isRequired,
  handleChangeQualificationReason: PropTypes.func.isRequired,
  handleChangeValueChecked: PropTypes.func.isRequired,
  checkedOptions: PropTypes.array.isRequired,
  checkFields: PropTypes.shape({
    qualificationReasonSpecies: PropTypes.bool
  }).isRequired,
  shouldRenderIbaDisqualifiedCheckbox: PropTypes.bool.isRequired,
  disqualifiedIbaTrigger: PropTypes.bool.isRequired,
  handleToggleDisqualifiedIbaTrigger: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  hasDisqualifyingReasonSelected: PropTypes.bool.isRequired
}

ReassessmentRationalSection.propTypes = {
  shouldShowIbaDisqualificationSection: PropTypes.bool.isRequired,
  reassessmentRational: PropTypes.string.isRequired,
  handleChangeReassessmentRational: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  shouldRequireDisqualifiedIbaDetails: PropTypes.bool.isRequired
}

SpeciesDetailsAssessmentForm.propTypes = {
  item: PropTypes.shape(speciesItemPropTypes).isRequired,
  getSiteSpecie: PropTypes.func.isRequired,
  setItem: PropTypes.func.isRequired,
  checkFields: PropTypes.shape({
    numberOfAssessmentParameters: PropTypes.bool,
    qualificationReasonSpecies: PropTypes.bool
  }).isRequired,
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool,
    ibaAccess: PropTypes.shape({
      hasAccess: PropTypes.bool
    })
  }).isRequired,
  setSiteProposalFieldValidation: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired
}

export default SpeciesDetailsAssessmentForm
